import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  SLEEP_TIMER_PRESETS_MINUTES,
  SleepTimerState,
} from '@/hooks/useSleepTimer';

interface SleepTimerMenuProps {
  id: string;
  sleepTimer: SleepTimerState | null;
  remainingSeconds: number | null;
  onSelectMinutes: (minutes: number) => void;
  onSelectEndOfChapter: () => void;
  onCancel: () => void;
  onClose: () => void;
  animationClassName?: string;
  placement?: 'above' | 'below';
}

export const formatSleepTimerRemaining = (remainingSeconds: number | null): string => {
  if (remainingSeconds === null) {
    return '';
  }
  if (remainingSeconds >= 60) {
    const minutes = Math.ceil(remainingSeconds / 60);
    return `${minutes} min remaining`;
  }
  return `0:${String(remainingSeconds).padStart(2, '0')} remaining`;
};

export const getSleepTimerButtonLabel = (
  sleepTimer: SleepTimerState | null,
  remainingSeconds: number | null
): string => {
  if (!sleepTimer) {
    return 'Set sleep timer';
  }
  if (sleepTimer.mode === 'end-of-chapter') {
    return 'Sleep timer active: end of chapter';
  }
  return `Sleep timer active: ${formatSleepTimerRemaining(remainingSeconds)}`;
};

const SleepTimerMenu: React.FC<SleepTimerMenuProps> = ({
  id,
  sleepTimer,
  remainingSeconds,
  onSelectMinutes,
  onSelectEndOfChapter,
  onCancel,
  onClose,
  animationClassName = '',
  placement = 'above',
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const activeDescription = sleepTimer?.mode === 'end-of-chapter'
    ? 'End of chapter'
    : sleepTimer?.mode === 'countdown'
      ? formatSleepTimerRemaining(remainingSeconds)
      : null;
  const optionClassName = (isActive: boolean) => `cursor-pointer rounded-md px-3 py-2.5 text-sm transition-colors ${
    isActive
      ? isDarkMode
        ? 'bg-gray-700 font-semibold text-gray-200'
        : 'bg-gray-200 font-semibold text-gray-700'
      : isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200/50'
  }`;

  const selectMinutes = (minutes: number) => {
    onSelectMinutes(minutes);
    onClose();
  };

  const selectEndOfChapter = () => {
    onSelectEndOfChapter();
    onClose();
  };

  return (
    <div
      id={id}
      className={`sleep-timer-menu absolute left-0 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-lg border p-1.5 shadow-xl ${
        placement === 'below' ? 'top-full mt-2' : 'bottom-full mb-2 md:left-1/2 md:-translate-x-1/2'
      } ${animationClassName} ${
        isDarkMode ? 'bg-zinc-800 border-zinc-700 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
      }`}
      style={{
        transformOrigin: placement === 'below' ? 'top left' : 'bottom center',
        touchAction: 'pan-y',
        willChange: 'transform, opacity',
      }}
      aria-label="Sleep timer"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <div>
          <h2 className="text-sm font-semibold">Sleep timer</h2>
          {activeDescription && (
            <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} role="status">
              {activeDescription}
            </p>
          )}
        </div>
        {sleepTimer && (
          <button
            type="button"
            className={`cursor-pointer rounded-md px-2 py-1 text-xs font-medium ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200/50'
            }`}
            onClick={() => {
              onCancel();
              onClose();
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-1">
        {SLEEP_TIMER_PRESETS_MINUTES.map((minutes) => (
          <button
            key={minutes}
            type="button"
            className={optionClassName(
              sleepTimer?.mode === 'countdown' && sleepTimer.minutes === minutes
            )}
            aria-pressed={sleepTimer?.mode === 'countdown' && sleepTimer.minutes === minutes}
            onClick={() => selectMinutes(minutes)}
          >
            {minutes} minutes
          </button>
        ))}
        <button
          type="button"
          className={`${optionClassName(sleepTimer?.mode === 'end-of-chapter')} col-span-2`}
          aria-pressed={sleepTimer?.mode === 'end-of-chapter'}
          onClick={selectEndOfChapter}
        >
          End of chapter
        </button>
      </div>
    </div>
  );
};

export default SleepTimerMenu;
